import { useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Fresh } from "./common";
import { useForecast, ForecastBadge } from "./Forecast";
import { TradeButtons } from "./SellHolding";

export const TFS = ["1m", "5m", "15m", "30m", "1h", "1d"];
const STYLE = { solid: LineStyle.Solid, dashed: LineStyle.Dashed, dotted: LineStyle.Dotted };
const OV_COLORS = { ema9: "#F472B6", ema21: "#F59E0B", ema50: "#3B82F6", ema200: "#A78BFA", sma20: "#94A3B8", vwap: "#22D3EE", bb_up: "#64748B", bb_lo: "#64748B", supertrend: "#10B981", psar: "#EAB308", kc_up: "#475569", kc_lo: "#475569", dc_hi: "#0EA5E9", dc_lo: "#0EA5E9", ichi_a: "#34D399", ichi_b: "#F87171" };

export function makeBus() {
  const subs = new Set();
  return { on: (f) => { subs.add(f); return () => subs.delete(f); }, emit: (m) => subs.forEach((f) => f(m)) };
}

export default function ChartPane({ id, symbol, tf, universe = [], onSymbol, onTf, bus, overlays = "ema21,ema50", showPatterns = true, height = 260, onData, forecast = false, hideTrade = false }) {
  const el = useRef(null);
  const chart = useRef(null);
  const candles = useRef(null);
  const extra = useRef([]);
  const plines = useRef([]);
  const dataRef = useRef([]);
  const syncing = useRef(false);
  const [info, setInfo] = useState(null);
  const fc = useForecast(chart, forecast, symbol, tf, !!info);

  useEffect(() => {
    const c = createChart(el.current, {
      autoSize: true,
      layout: { background: { color: "#FFFFFF" }, textColor: "#475569", fontFamily: "JetBrains Mono", fontSize: 10 },
      grid: { vertLines: { color: "#F1F5F9" }, horzLines: { color: "#F1F5F9" } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: "#E2E8F0" },
      timeScale: { borderColor: "#E2E8F0", timeVisible: true, secondsVisible: false },
    });
    candles.current = c.addCandlestickSeries({ upColor: "#10B981", downColor: "#EF4444", borderVisible: false, wickUpColor: "#10B981", wickDownColor: "#EF4444" });
    chart.current = c;
    c.subscribeCrosshairMove((p) => {
      if (syncing.current || !bus) return;
      bus.emit({ from: id, time: p.time || null });
    });
    const off = bus?.on((m) => {
      if (m.from === id || !chart.current) return;
      syncing.current = true;
      const bar = m.time && dataRef.current.find((d) => d.time >= m.time);
      try {
        if (bar) chart.current.setCrosshairPosition(bar.close, bar.time, candles.current);
        else chart.current.clearCrosshairPosition();
      } catch (e) { /* ignore */ }
      syncing.current = false;
    });
    return () => { off && off(); c.remove(); chart.current = null; };
  }, [id, bus]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/market/chart", { params: { symbol, tf, overlays, patterns: showPatterns } });
        if (!alive || !chart.current) return;
        render(r.data);
        setInfo(r.data);
        onData && onData(r.data);
      } catch (e) { /* keep last */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, tf, overlays, showPatterns]);

  const render = (d) => {
    const c = chart.current;
    extra.current.forEach((s) => { try { c.removeSeries(s); } catch (e) { /* */ } });
    extra.current = [];
    plines.current.forEach((pl) => { try { candles.current.removePriceLine(pl); } catch (e) { /* */ } });
    plines.current = [];
    dataRef.current = d.candles;
    candles.current.setData(d.candles);
    Object.entries(d.overlays || {}).forEach(([k, pts]) => {
      const s = c.addLineSeries({ color: OV_COLORS[k] || "#94A3B8", lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
      s.setData(pts); extra.current.push(s);
    });
    const markers = [];
    (d.patterns || []).forEach((p) => {
      (p.lines || []).forEach((ln) => {
        if (!ln.points || ln.points.length < 2) return;
        const s = c.addLineSeries({ color: ln.color, lineWidth: p.status === "INVALIDATED" || p.status === "EXPIRED" ? 1 : 2, lineStyle: STYLE[ln.style] ?? LineStyle.Solid, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
        s.setData(ln.points); extra.current.push(s);
      });
      if (p.category === "candlestick" && p.marker_time) {
        markers.push({ time: p.marker_time, position: p.direction === "bearish" ? "aboveBar" : "belowBar", color: p.direction === "bearish" ? "#EF4444" : p.direction === "bullish" ? "#10B981" : "#94A3B8", shape: "circle", text: p.type.split(" ").map((w) => w[0]).join("") });
      }
    });
    const times = d.candles.map((x) => x.time);
    (d.markers || []).forEach((m) => {
      const snap = times.filter((x) => x <= m.time).pop();
      if (snap) markers.push({ time: snap, position: m.side === "buy" ? "belowBar" : "aboveBar", color: m.side === "buy" ? "#3B82F6" : "#F59E0B", shape: m.side === "buy" ? "arrowUp" : "arrowDown", text: m.side === "buy" ? "BUY" : m.reason?.toUpperCase().slice(0, 8) });
    });
    (d.signals || []).forEach((sg) => {
      const snap = times.filter((x) => x <= sg.time).pop();
      if (snap) markers.push({ time: snap, position: sg.action === "BUY" ? "belowBar" : "aboveBar", color: sg.action === "BUY" ? "#16A34A" : "#DC2626", shape: sg.action === "BUY" ? "arrowUp" : "arrowDown", text: sg.action, size: 1.5 });
    });
    markers.sort((a, b) => a.time - b.time);
    candles.current.setMarkers(markers);
    (d.lines || []).forEach((l) => plines.current.push(candles.current.createPriceLine({ price: l.price, color: l.color, lineWidth: 1, lineStyle: ["ENTRY", "BELI", "SHORT"].includes(l.label) ? LineStyle.Solid : LineStyle.Dashed, title: `${l.label} ${l.price}` })));
    const lp = (d.lines || []).map((l) => l.price).filter(Boolean);
    if (lp.length && times.length) {
      const s = c.addLineSeries({ color: "rgba(0,0,0,0)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
      s.setData([{ time: times[0], value: Math.max(...lp) }, { time: times[times.length - 1], value: Math.min(...lp) }]); extra.current.push(s);
    }
  };

  const last = info?.candles?.[info.candles.length - 1];
  const first = info?.candles?.[info.candles.length - 2];
  const chg = last && first ? ((last.close - first.close) / first.close) * 100 : 0;
  return (
    <div className="panel flex flex-col p-0" data-testid={`chart-pane-${id}`}>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[#E2E8F0] px-2 py-1.5">
        <select data-testid={`chart-symbol-select-${id}`} value={symbol} onChange={(e) => onSymbol && onSymbol(e.target.value)} className="mono rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-1 py-0.5 text-[11px] text-slate-900 outline-none">
          {universe.map((u) => <option key={u.symbol} value={u.symbol}>{u.symbol}</option>)}
        </select>
        <div className="flex">
          {TFS.map((x) => (
            <button key={x} data-testid={`chart-tf-${id}-${x}`} onClick={() => onTf && onTf(x)} className={`mono px-1.5 py-0.5 text-[10px] ${tf === x ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"}`}>{x}</button>
          ))}
        </div>
        {last && <span className="mono text-[11px] text-slate-800">{last.close}</span>}
        {last && <span className={`mono text-[10px] ${chg >= 0 ? "text-emerald-600" : "text-red-600"}`}>{chg >= 0 ? "+" : ""}{chg.toFixed(2)}%</span>}
        <span className="ml-auto" />
        <Fresh meta={info?.meta} testid={`chart-fresh-${id}`} />
        <button title="Detach to new window" data-testid={`chart-popout-${id}`} onClick={() => window.open(`/chart-window?symbol=${encodeURIComponent(symbol)}&tf=${tf}`, `chart-${symbol}-${tf}`, "width=1100,height=700")} className="text-slate-500 hover:text-slate-800">
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      <div ref={el} style={{ height, minHeight: height }} className="w-full shrink-0" />
      <div className="flex flex-wrap gap-1 border-t border-[#E2E8F0] px-2 py-1">
        {info?.position && <span className="mono text-[10px] text-blue-600">POS {info.position.qty} @ {info.position.avg_entry} · SL {info.position.stop}</span>}
        {forecast && <ForecastBadge fc={fc} id={id} />}
        {!hideTrade && <TradeButtons symbol={symbol} />}
        {(info?.patterns || []).filter((p) => !["INVALIDATED", "EXPIRED"].includes(p.status) && p.category !== "level").slice(0, 4).map((p) => (
          <span key={p.key} className={`mono rounded-sm px-1 text-[9px] ${p.direction === "bullish" ? "bg-emerald-500/10 text-emerald-600" : p.direction === "bearish" ? "bg-red-500/10 text-red-600" : "bg-slate-500/10 text-slate-600"}`}>{p.type} · {p.status}</span>
        ))}
      </div>
    </div>
  );
}
